import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2, ShieldCheck, KeyRound, DownloadCloud, ArrowLeft, Star, Ticket, ShoppingCart } from "lucide-react";
import api from "@/lib/api";
import { IMG } from "@/lib/imagery";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { getRef } from "@/lib/affiliate";
import BrokerCTA from "@/components/BrokerCTA";
import Seo from "@/components/Seo";

const PROVIDERS = [
  { id: "pesapal", label: "PesaPal", note: "Cards & mobile money" },
  { id: "nowpayments", label: "NowPayments", note: "Crypto (BTC, ETH, USDT)" },
];

export default function ProductDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { add: addToCart, has: inCart } = useCart();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [provider, setProvider] = useState("pesapal");
  const [buying, setBuying] = useState(false);
  const [reviews, setReviews] = useState({ reviews: [], rating_avg: 0, rating_count: 0 });
  const [coupon, setCoupon] = useState("");
  const [couponInfo, setCouponInfo] = useState(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [plan, setPlan] = useState("monthly");

  const isDual = product?.billing_period === "both";
  const isLifetime = product?.billing_period === "lifetime";
  const activePrice = product ? (isDual ? (plan === "yearly" ? Number(product.price_yearly || 0) : Number(product.price)) : Number(product.price)) : 0;
  const periodSuffix = isDual ? (plan === "yearly" ? "/yr" : "/mo") : (product && ["monthly", "yearly"].includes(product.billing_period) ? (product.billing_period === "monthly" ? "/mo" : "/yr") : "");

  useEffect(() => {
    api.get(`/products/${slug}`)
      .then((r) => setProduct(r.data))
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
    api.get(`/products/${slug}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  }, [slug]);

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    setApplyingCoupon(true);
    try {
      const { data } = await api.post("/coupons/validate", {
        code: coupon.trim(),
        product_ids: [product.id],
        subtotal: activePrice,
      });
      setCouponInfo(data);
      toast.success(`Coupon applied — you save $${data.discount.toFixed(2)}`);
    } catch (e) {
      setCouponInfo(null);
      toast.error(e?.response?.data?.detail || "Invalid coupon");
    } finally { setApplyingCoupon(false); }
  };

  const downloadFile = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/products/${slug}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : slug;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Download started — run it on a live account when you're ready.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    } finally { setDownloading(false); }
  };

  const buy = async () => {
    if (!user) {
      toast.info("Sign in to complete purchase");
      navigate("/login");
      return;
    }
    setBuying(true);
    try {
      const { data } = await api.post("/orders", {
        items: [{ product_id: product.id, quantity: 1, plan: isDual ? plan : undefined }],
        provider,
        coupon_code: couponInfo ? coupon.trim() : undefined,
        ref_code: getRef() || undefined,
      });
      if (data.checkout_url && /^https?:\/\//.test(data.checkout_url)) {
        window.location.href = data.checkout_url;
      } else {
        navigate(`/checkout/pay/${data.id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start checkout");
    } finally {
      setBuying(false);
    }
  };

  if (loading) return <div className="p-24 text-center text-emerald-100/50">Loading…</div>;
  if (!product) return <div className="p-24 text-center text-emerald-100/50">Product not found.</div>;

  return (
    <div className="relative mx-auto max-w-7xl px-5 py-12 lg:px-8 lg:py-20">
      <Seo title={product.title} description={product.subtitle || (product.description || "").slice(0, 155)} image={product.image_url} type="product" />
      <Link to={`/store/${product.category_slug}`} className="inline-flex items-center gap-1.5 text-xs text-emerald-100/60 hover:text-lime-300">
        <ArrowLeft size={12} /> Back to {product.category_slug}
      </Link>

      <div className="mt-6 grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:gap-16">
        {/* Image */}
        <div className="photo-vignette h-[380px] lg:h-[520px]">
          <img src={product.image_url || IMG.matrix} alt="" className="h-full w-full object-cover" />
        </div>

        {/* Details */}
        <div>
          <p className="overline mb-3" data-testid="product-category">/ {product.category_slug}</p>
          <h1 data-testid="product-title" className="font-editorial text-4xl font-medium tracking-tight text-white sm:text-5xl lg:text-6xl">
            {product.title}
          </h1>
          {product.subtitle && (
            <p className="mt-3 text-base text-emerald-100/70">{product.subtitle}</p>
          )}

          <div className="mt-6 flex items-baseline gap-3">
            <span className="font-editorial text-4xl text-lime-300">${activePrice.toFixed(2)}</span>
            <span className="text-xs text-emerald-100/50">{product.currency}{periodSuffix}</span>
            <span className="chip chip-emerald ml-2">
              {product.license_type === "account_bound" ? "Account-bound" : "One-time"}
            </span>
            {isLifetime && <span className="chip chip-emerald ml-1" data-testid="lifetime-badge">Lifetime · Best value</span>}
          </div>

          {isLifetime && (
            <p className="mt-2 text-xs font-medium text-lime-300" data-testid="lifetime-note">One-time payment — lifetime access, no renewals.</p>
          )}

          {isDual && (
            <div className="mt-5 inline-flex rounded-full border border-emerald-500/20 bg-[#0d2416]/50 p-1" data-testid="plan-toggle">
              {[{ k: "monthly", l: "Monthly", p: product.price }, { k: "yearly", l: "Yearly", p: product.price_yearly }].map((o) => (
                <button key={o.k} onClick={() => { setPlan(o.k); setCouponInfo(null); }} data-testid={`plan-${o.k}`}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${plan === o.k ? "bg-lime-400 text-[#06210f]" : "text-emerald-100/70 hover:text-white"}`}>
                  {o.l} · ${Number(o.p || 0).toFixed(2)}
                  {o.k === "yearly" && (
                    <span className={`ml-2 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${plan === "yearly" ? "bg-[#06210f] text-lime-300" : "bg-lime-400/20 text-lime-300"}`} data-testid="best-value-badge">Best value</span>
                  )}
                </button>
              ))}
            </div>
          )}
          {isDual && Number(product.price_yearly) > 0 && Number(product.price) > 0 && plan === "yearly" && (() => {
            const saving = product.price * 12 - product.price_yearly;
            const months = Math.round(saving / product.price);
            return saving > 0 ? (
              <p className="mt-2 text-xs font-medium text-lime-300" data-testid="yearly-savings">
                Save ${saving.toFixed(0)} a year{months > 0 ? ` — about ${months} month${months === 1 ? "" : "s"} free` : ""}.
              </p>
            ) : null;
          })()}

          <div className="prose-edge mt-8 max-w-xl">
            <p>{product.description}</p>
          </div>

          {product.features?.length > 0 && (
            <ul className="mt-6 grid gap-2">
              {product.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-emerald-100/80">
                  <Check size={14} className="mt-1 text-lime-300" /> {f}
                </li>
              ))}
            </ul>
          )}

          {/* Provider select */}
          <div className="mt-10">
            <p className="overline mb-3">Choose provider</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {PROVIDERS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setProvider(p.id)}
                  data-testid={`provider-${p.id}`}
                  className={`rounded-xl border p-4 text-left transition-colors ${
                    provider === p.id
                      ? "border-lime-400/60 bg-emerald-500/10"
                      : "border-emerald-500/15 bg-[#0d2416]/50 hover:border-emerald-500/40"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-display font-medium text-white">{p.label}</span>
                    <span className={`h-3 w-3 rounded-full border ${provider === p.id ? "border-lime-300 bg-lime-300" : "border-emerald-500/40"}`} />
                  </div>
                  <p className="mt-1 text-xs text-emerald-100/60">{p.note}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Coupon */}
          {!product.owned && !product.admin_preview && (
            <div className="mt-6">
              <p className="overline mb-3">Have a coupon?</p>
              <div className="flex flex-wrap gap-2">
                <input
                  value={coupon}
                  onChange={(e) => { setCoupon(e.target.value); setCouponInfo(null); }}
                  placeholder="Enter code"
                  data-testid="coupon-input"
                  className="flex-1 min-w-[160px] rounded-xl border border-emerald-500/15 bg-[#0d2416]/50 px-3 py-2.5 text-sm text-white focus:border-lime-400/60 focus:outline-none"
                />
                <button onClick={applyCoupon} disabled={applyingCoupon || !coupon.trim()} data-testid="coupon-apply" className="btn-edge-ghost text-sm disabled:opacity-40">
                  {applyingCoupon ? <Loader2 size={14} className="animate-spin" /> : <Ticket size={14} />} Apply
                </button>
              </div>
              {couponInfo && (
                <p className="mt-2 text-sm text-lime-300" data-testid="coupon-result">
                  Coupon <span className="font-mono-edge">{couponInfo.code}</span> — you save ${couponInfo.discount.toFixed(2)}. New total: <strong>${(product.price - couponInfo.discount).toFixed(2)}</strong>
                </p>
              )}
            </div>
          )}

          <div className="mt-8 flex flex-wrap gap-3">
            {(product.owned || product.admin_preview) && !["monthly", "yearly"].includes(product.billing_period) ? (
              <>
                {product.has_download && (
                  <button onClick={downloadFile} disabled={downloading} data-testid="product-download" className="btn-edge">
                    {downloading ? <Loader2 size={16} className="animate-spin" /> : <DownloadCloud size={16} />} Download
                  </button>
                )}
                <span className="chip chip-emerald self-center" data-testid="product-owned">{product.admin_preview ? "Admin preview" : "You own this"}</span>
              </>
            ) : (
              <>
                <button
                  onClick={buy}
                  disabled={buying}
                  data-testid="product-buy"
                  className="btn-edge"
                >
                  {buying ? <Loader2 size={16} className="animate-spin" /> : <>{product.owned ? "Renew" : "Buy"} for ${(couponInfo ? activePrice - couponInfo.discount : activePrice).toFixed(2)}{periodSuffix}</>}
                </button>
                {product.owned && product.has_download && (
                  <button onClick={downloadFile} disabled={downloading} data-testid="product-download" className="btn-edge-ghost">
                    {downloading ? <Loader2 size={16} className="animate-spin" /> : <DownloadCloud size={16} />} Download
                  </button>
                )}
                {!product.owned && (
                  <button
                    onClick={() => { addToCart(product, isDual ? plan : null); }}
                    data-testid="product-add-cart"
                    className="btn-edge-ghost"
                  >
                    <ShoppingCart size={16} /> {inCart(product.id) ? "In cart" : "Add to cart"}
                  </button>
                )}
              </>
            )}
            <Link to={`/store/${product.category_slug}`} className="btn-edge-ghost">Keep browsing</Link>
          </div>

          <div className="mt-8 grid gap-3">
            <TrustRow icon={ShieldCheck} label="Account-bound licensing" />
            <TrustRow icon={KeyRound} label="Rebind up to 3× included" />
            <TrustRow icon={DownloadCloud} label="Instant download after payment" />
          </div>

          {["expert-advisors", "indicators"].includes(product.category_slug) && (
            <div className="mt-8">
              <BrokerCTA src={`product-${product.slug}`} compact />
            </div>
          )}
        </div>
      </div>

      <ReviewsSection product={product} data={reviews} onPosted={() => api.get(`/products/${slug}/reviews`).then((r) => setReviews(r.data))} />
    </div>
  );
}

function Stars({ value, size = 14 }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={size} className={n <= Math.round(value) ? "fill-[color:var(--flare)] text-[color:var(--flare)]" : "text-[color:var(--ink-hairline)]"} />
      ))}
    </span>
  );
}

function ReviewsSection({ product, data, onPosted }) {
  const { user } = useAuth();
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/products/${product.id}/reviews`, { rating, title, body });
      toast.success("Thanks for your review");
      setTitle(""); setBody("");
      onPosted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to post review");
    } finally { setBusy(false); }
  };

  return (
    <section className="mt-16 border-t border-[color:var(--ink-hairline)] pt-12" data-testid="reviews-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="overline mb-1">Reviews</p>
          <div className="flex items-center gap-3">
            <span className="font-editorial text-3xl text-[color:var(--ink)]">{data.rating_avg || "—"}</span>
            <Stars value={data.rating_avg} size={16} />
            <span className="text-sm text-[color:var(--ink-muted)]">{data.rating_count} review{data.rating_count === 1 ? "" : "s"}</span>
          </div>
        </div>
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_1.2fr]">
        <div className="card-moss p-6">
          <p className="overline mb-3">Write a review</p>
          {user ? (
            <form onSubmit={submit} className="grid gap-3" data-testid="review-form">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button type="button" key={n} onClick={() => setRating(n)} data-testid={`review-star-${n}`} aria-label={`${n} stars`}>
                    <Star size={22} className={n <= rating ? "fill-[color:var(--flare)] text-[color:var(--flare)]" : "text-[color:var(--ink-hairline)]"} />
                  </button>
                ))}
              </div>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Headline (optional)" data-testid="review-title"
                className="w-full rounded-md border border-[color:var(--ink-hairline)] bg-white px-3 py-2 text-sm text-[color:var(--ink)] focus:border-[color:var(--evergreen)] focus:outline-none" />
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="How did this perform for you?" data-testid="review-body"
                className="w-full rounded-md border border-[color:var(--ink-hairline)] bg-white px-3 py-2 text-sm text-[color:var(--ink)] focus:border-[color:var(--evergreen)] focus:outline-none" />
              <button disabled={busy} data-testid="review-submit" className="btn-edge justify-center">
                {busy ? <Loader2 size={14} className="animate-spin" /> : "Post review"}
              </button>
              <p className="text-[11px] text-[color:var(--ink-muted)]">Only verified buyers can post. Reviews are tied to your account.</p>
            </form>
          ) : (
            <p className="text-sm text-[color:var(--ink-body)]">
              <Link to="/login" className="font-medium text-[color:var(--evergreen)] hover:underline">Sign in</Link> and purchase this product to leave a review.
            </p>
          )}
        </div>

        <div className="grid gap-3">
          {data.reviews.length === 0 ? (
            <div className="card-edge p-6 text-sm text-[color:var(--ink-muted)]">No reviews yet — be the first verified buyer to weigh in.</div>
          ) : (
            data.reviews.map((r) => (
              <div key={r.id} className="card-edge p-5" data-testid={`review-item-${r.id}`}>
                <div className="flex items-center justify-between">
                  <Stars value={r.rating} />
                  <span className="text-[11px] text-[color:var(--ink-muted)]">{new Date(r.created_at).toLocaleDateString()}</span>
                </div>
                {r.title && <p className="mt-2 font-editorial text-lg font-semibold text-[color:var(--ink)]">{r.title}</p>}
                {r.body && <p className="mt-1 text-sm text-[color:var(--ink-body)]">{r.body}</p>}
                <p className="mt-2 text-[11px] text-[color:var(--ink-muted)]">— {r.user_name}</p>
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
}

function TrustRow({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-3 text-sm text-emerald-100/80">
      <span className="grid h-8 w-8 place-items-center rounded-lg bg-emerald-500/12 text-lime-300">
        <Icon size={14} />
      </span>
      {label}
    </div>
  );
}
